import { useCallback, useEffect, useState } from 'react';
import { ConfirmDialog } from '../components/ConfirmDialog.js';

/**
 * ehr#333: the Credify confirm dialog and error toast, callable from any handler in place of the
 * browser's own window.confirm / alert. `<DialogHost />` is mounted once in main.tsx.
 *
 *   if (!(await askConfirm({ message: 'Delete “X”?', confirmLabel: 'Delete' }))) return;
 *   showError('Could not save.');
 */
export interface ConfirmOpts {
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button (the default); false gives the green one. */
  danger?: boolean;
}

export interface HostState {
  confirm: (ConfirmOpts & { resolve: (ok: boolean) => void }) | null;
  toast: { id: number; text: string } | null;
}

let state: HostState = { confirm: null, toast: null };
const listeners = new Set<(s: HostState) => void>();
function set(patch: Partial<HostState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l(state));
}

/** The host's subscription (also lets tests stand in for a mounted host). */
export function subscribeDialogs(fn: (s: HostState) => void): () => void {
  listeners.add(fn);
  fn(state);
  return () => {
    listeners.delete(fn);
  };
}

/** Answer the open confirm (the dialog's buttons, Esc, or a click outside). */
export function answerConfirm(ok: boolean) {
  const c = state.confirm;
  if (!c) return;
  set({ confirm: null });
  c.resolve(ok);
}

export function dismissToast() {
  set({ toast: null });
}

/** Resolves true when the user confirms; false on Cancel, Esc or a click outside. */
export function askConfirm(opts: ConfirmOpts | string): Promise<boolean> {
  const o = typeof opts === 'string' ? { message: opts } : opts;
  if (!listeners.size) return Promise.resolve(false); // no host mounted: never act unconfirmed
  state.confirm?.resolve(false); // a newer prompt replaces one still unanswered
  return new Promise((resolve) => set({ confirm: { ...o, resolve } }));
}

let toastSeq = 0;
/** A red toast at the bottom of the screen that stays 6 seconds (or until dismissed). */
export function showError(text: string) {
  if (!listeners.size) {
    console.error(text);
    return;
  }
  set({ toast: { id: ++toastSeq, text } });
}

/** The message of a caught error, for showError. */
export const errorText = (e: unknown, fallback = 'Something went wrong.') =>
  e instanceof Error && e.message ? e.message : typeof e === 'string' && e ? e : fallback;

export function DialogHost() {
  const [s, setS] = useState(state);
  useEffect(() => subscribeDialogs(setS), []);

  const toastId = s.toast?.id;
  useEffect(() => {
    if (toastId === undefined) return;
    const t = setTimeout(() => {
      if (state.toast?.id === toastId) dismissToast();
    }, 6000);
    return () => clearTimeout(t);
  }, [toastId]);

  const c = s.confirm;
  const onConfirm = useCallback(() => answerConfirm(true), []);
  const onClose = useCallback(() => answerConfirm(false), []);

  return (
    <>
      {c && (
        <ConfirmDialog
          message={c.message}
          confirmLabel={c.confirmLabel ?? 'Delete'}
          cancelLabel={c.cancelLabel ?? 'Cancel'}
          danger={c.danger ?? true}
          onConfirm={onConfirm}
          onClose={onClose}
        />
      )}
      {s.toast && (
        <div className="cred-toast-err" role="alert" key={s.toast.id}>
          <span>{s.toast.text}</span>
          <button type="button" aria-label="Dismiss" onClick={dismissToast}>
            ×
          </button>
        </div>
      )}
    </>
  );
}
